"use client";

import { useState, ReactNode, useRef, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

interface ExpandablePortableTextProps {
  collapsedContent: ReactNode;
  expandedContent: ReactNode;
  hasMore: boolean;
}

export default function ExpandablePortableText({
  collapsedContent,
  expandedContent,
  hasMore,
}: ExpandablePortableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<string>("auto");
  const [expandedHeight, setExpandedHeight] = useState<string>("auto");
  const collapsedRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (collapsedRef.current) {
      setCollapsedHeight(`${collapsedRef.current.scrollHeight}px`);
    }
    if (expandedRef.current) {
      setExpandedHeight(`${expandedRef.current.scrollHeight}px`);
    }
  }, [collapsedContent, expandedContent]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      <div className="prose max-w-none text-gray-700">
        <div
          ref={collapsedRef}
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{
            maxHeight: isExpanded ? "0px" : collapsedHeight,
            opacity: isExpanded ? 0 : 1,
          }}
        >
          {collapsedContent}
        </div>
        <div
          ref={expandedRef}
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{
            maxHeight: isExpanded ? expandedHeight : "0px",
            opacity: isExpanded ? 1 : 0,
          }}
        >
          {expandedContent}
        </div>
      </div>
      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleToggle}
            className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <>
                Show Less
                <FaChevronUp className="h-4 w-4 transition-transform duration-300" />
              </>
            ) : (
              <>
                Show More
                <FaChevronDown className="h-4 w-4 transition-transform duration-300" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

